/**
 * Elementi condivisi per i contenuti del Piano Editoriale
 * (usati da PED, PED Storie, Calendario, Pubblicazioni, Il mio lavoro).
 *
 * Un solo vocabolario visivo: stesso colore per formato, stesso modo di scrivere
 * le date ("Oggi", "Domani", "gio 26 set"), stessa anteprima del contenuto.
 */
import React from 'react';
import { Clock, CheckCircle2, AlertCircle, CircleDashed, Image as ImageIcon, Film, ExternalLink, MessageSquare, UserRound, Clapperboard } from 'lucide-react';
import { FaseBadge } from '../lib/produzione';
import { Modal } from './ui';

// Colore per formato: tinte medie, leggibili sia sul tema scuro sia sul chiaro.
export const FORMATO_COLOR = {
  'Reel': '#8b5cf6',
  'Carosello': '#0ea5e9',
  'Storia': '#f59e0b',
  'Post Singolo': '#10b981',
  'Articolo SEO': '#3b82f6',
  'Video YT': '#f43f5e',
  'Newsletter': '#14b8a6',
};
export const formatoColor = (f) => FORMATO_COLOR[f] || '#94a3b8';

// Stato di pubblicazione su PostProxy (tabella pubblicazioni_social)
export const PUB_STATO = {
  published: { tone: 'green', color: 'var(--green)', bg: 'var(--green-dim)', label: 'Pubblicato', Icon: CheckCircle2 },
  scheduled: { tone: 'cyan', color: 'var(--cyan)', bg: 'var(--cyan-dim)', label: 'Programmato', Icon: Clock },
  processing: { tone: 'cyan', color: 'var(--cyan)', bg: 'var(--cyan-dim)', label: 'In corso', Icon: Clock },
  failed: { tone: 'red', color: 'var(--red)', bg: 'var(--red-dim)', label: 'Errore', Icon: AlertCircle },
  pending: { tone: 'neutral', color: 'var(--text-muted)', bg: 'var(--bg3)', label: 'In attesa', Icon: CircleDashed },
  none: { tone: 'neutral', color: 'var(--text-muted)', bg: 'var(--bg3)', label: 'Da pubblicare', Icon: CircleDashed },
};

export const hhmm = (t) => (t ? String(t).slice(0, 5) : '');
export const isVideo = (u) => /\.(mp4|mov|webm|m4v)(\?|$)/i.test(u || '');
export const pad2 = (n) => String(n).padStart(2, '0');
export const toDateStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
export const todayStr = () => toDateStr(new Date());

export function mediaList(r) {
  const m = r?.media_url;
  if (Array.isArray(m)) return m.filter(Boolean);
  if (typeof m === 'string') { try { const p = JSON.parse(m); return Array.isArray(p) ? p.filter(Boolean) : []; } catch { return []; } }
  return [];
}

// "Oggi", "Domani", "Ieri", altrimenti "gio 26 set" (+ anno se diverso)
export function giornoLabel(ds, { long = false } = {}) {
  if (!ds) return 'Senza data';
  const d = new Date(`${String(ds).slice(0, 10)}T00:00:00`);
  const oggi = new Date(); oggi.setHours(0, 0, 0, 0);
  const diff = Math.round((d - oggi) / 86400000);
  if (diff === 0) return 'Oggi';
  if (diff === 1) return 'Domani';
  if (diff === -1) return 'Ieri';
  const opts = long
    ? { weekday: 'long', day: 'numeric', month: 'long' }
    : { weekday: 'short', day: 'numeric', month: 'short' };
  if (d.getFullYear() !== oggi.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString('it-IT', opts);
}

export function FormatTag({ formato, style }) {
  return <span className="eden-fmt" style={{ '--fmt': formatoColor(formato), ...style }}>{formato || 'Post'}</span>;
}

export function PubPill({ stato }) {
  const s = PUB_STATO[stato || 'none'] || PUB_STATO.none;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, padding: '3px 9px', borderRadius: 999, color: s.color, background: s.bg, whiteSpace: 'nowrap' }}>
      <s.Icon size={12} strokeWidth={2.4} />{s.label}
    </span>
  );
}

// Miniatura del primo media (o pallino del formato se non c'è media)
export function Thumb({ r, size = 48 }) {
  const media = mediaList(r);
  return (
    <div style={{
      width: size, height: size, borderRadius: 10, overflow: 'hidden', flexShrink: 0,
      background: 'var(--bg3)', border: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative',
    }}>
      {media[0]
        ? (isVideo(media[0])
          ? <video src={media[0]} muted preload="metadata" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <img src={media[0]} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />)
        : <ImageIcon size={Math.round(size * 0.36)} color="var(--text-dim)" />}
      {media.length > 1 && (
        <span style={{ position: 'absolute', right: 3, bottom: 3, fontSize: 9, fontWeight: 800, padding: '0 4px', borderRadius: 5, background: 'rgba(0,0,0,0.65)', color: '#fff' }}>
          {media.length}
        </span>
      )}
    </div>
  );
}

/**
 * Anteprima rapida di un contenuto (dal calendario): media, testo, stato, "Apri".
 * Su telefono si apre come foglio dal basso (Modal del kit UI).
 */
export function ContentPreview({ r, onOpen, onClose, extra }) {
  const media = r ? mediaList(r) : [];
  const ora = r ? hhmm(r.ora_pubblicazione) : '';
  return (
    <Modal open={!!r} onClose={onClose} width={460}>
      {r && (
        <div style={{ margin: -18 }}>
          <div style={{ position: 'relative', height: media[0] ? 200 : 0, background: 'var(--bg3)', overflow: 'hidden' }}>
            {media[0] && (isVideo(media[0])
              ? <video src={media[0]} muted controls style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : <img src={media[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />)}
            {media.length > 1 && (
              <span style={{ position: 'absolute', bottom: 8, right: 10, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {isVideo(media[0]) && <Film size={11} />}+{media.length - 1}
              </span>
            )}
          </div>
          <div style={{ padding: '16px 18px 18px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
              <FormatTag formato={r.formato} />
              {r.cliente_nome && <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{r.cliente_nome}</span>}
              {r.id_post && <span style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: 'var(--text-dim)' }}>{r.id_post}</span>}
            </div>
            <div style={{ fontSize: 17, fontWeight: 650, color: 'var(--text)', lineHeight: 1.3, letterSpacing: '-0.01em' }}>{r.titolo_post || 'Contenuto'}</div>
            {r.hook && <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>{r.hook}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: 'var(--text-muted)' }}>
                <Clock size={13} /> {giornoLabel(r.data_pubblicazione, { long: true })}{ora ? ` · ${ora}` : ''}
              </span>
              {r.pub_stato && <PubPill stato={r.pub_stato} />}
            </div>
            {r.pub_error && r.pub_stato === 'failed' && (
              <div style={{ fontSize: 12, color: 'var(--red)', background: 'var(--red-dim)', borderRadius: 8, padding: '7px 10px', marginTop: 10 }}>{String(r.pub_error).slice(0, 200)}</div>
            )}
            {r.caption_pubblica && (
              <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-muted)', background: 'var(--bg3)', borderRadius: 9, padding: '9px 11px', maxHeight: 110, overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {r.caption_pubblica}
              </div>
            )}
            {extra}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button className="eden-btn is-primary" style={{ flex: 1 }} onClick={() => onOpen(r)}>
                <ExternalLink size={15} /> Apri contenuto
              </button>
              <button className="eden-btn" onClick={onClose}>Chiudi</button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * Segnali di collaborazione su un contenuto: fase di produzione video, responsabile
 * (SMM) e videomaker, commenti di revisione aperti, approvazione del cliente dal portale.
 * Compaiono solo quando c'è qualcosa da dire.
 */
export function SegnaliContenuto({ r, compatto = false }) {
  const appro = r?.cliente_appro;
  const aperti = Number(r?.commenti_aperti || 0);
  const nome = (n) => (n || '').trim().split(' ')[0];
  if (!appro && !aperti && !r?.fase_produzione && !r?.responsabile_nome && !r?.videomaker_nome) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-dim)' }}>
      {r.fase_produzione && <FaseBadge fase={r.fase_produzione} style={{ fontSize: 10.5, padding: '1px 7px' }} />}
      {appro && (
        <span title={r.cliente_feedback ? `Cliente: «${r.cliente_feedback}»` : undefined}
          style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999,
            background: appro === 'approvato' ? 'var(--green-dim)' : 'var(--yellow-dim)',
            color: appro === 'approvato' ? 'var(--green)' : 'var(--yellow)' }}>
          {appro === 'approvato' ? 'Cliente ✓' : 'Cliente: modifiche'}
        </span>
      )}
      {aperti > 0 && (
        <span title={`${aperti} commenti da risolvere`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--yellow)' }}>
          <MessageSquare size={12} />{aperti}
        </span>
      )}
      {!compatto && r.responsabile_nome && (
        <span title={`Responsabile: ${r.responsabile_nome}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
          <UserRound size={12} />{nome(r.responsabile_nome)}
        </span>
      )}
      {!compatto && r.videomaker_nome && (
        <span title={`Videomaker: ${r.videomaker_nome}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' }}>
          <Clapperboard size={12} />{nome(r.videomaker_nome)}
        </span>
      )}
    </span>
  );
}
